/**
 * <s-product-gallery> — single-photo slider for the product media track.
 *
 * Required markup:
 *   [data-gallery-track]            — horizontal scroll-snap track; its children are the slides.
 *   button[data-gallery-thumb]      — commandfor="<this id>" command="--go" data-index="<slide index>" (dot indicators).
 *   button[command="--prev" | "--next"] — optional arrows, commandfor="<this id>".
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #current = 0;
  #track;

  connectedCallback() {
    this.#ac = new AbortController();
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;
    this.#track = track;

    this.addEventListener('command', (event) => {
      const count = track.children.length;
      let index;
      if (event.command === '--go') {
        index = Number(event.source?.dataset.index ?? 0);
      } else if (event.command === '--next') {
        index = (this.#current + 1) % count;
      } else if (event.command === '--prev') {
        index = (this.#current - 1 + count) % count;
      } else {
        return;
      }
      this.#goTo(index);
    }, { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#setCurrent(Array.prototype.indexOf.call(track.children, entry.target));
        }
      }
    }, { root: track, threshold: 0.6 });

    for (const slide of track.children) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #goTo(index) {
    const track = this.#track;
    const slide = track.children[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track so the page itself never jumps.
    const offset = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  }

  #setCurrent(index) {
    if (index < 0) return;
    this.#current = index;
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
